// frontend/src/errors/useErrorHandler.ts
// Main hook for consistent error handling throughout the app

import { useCallback } from 'react';
import { useGlobalToast } from '../App';
import { useErrorContext } from './ErrorContext';
import {
  AppError,
  ErrorCategory,
  ErrorSeverity,
  ErrorDisplayType,
  ErrorHandlerOptions,
  ApiErrorResponse,
} from './errorTypes';
import {
  ERROR_MESSAGES,
  getMessageForStatusCode,
  getCategoryFromStatusCode,
  isRetryableError,
} from './errorMessages';

export const useErrorHandler = () => {
  const { showError } = useGlobalToast();
  const { setError: setGlobalError, clearError: clearGlobalError } =
    useErrorContext();

  /**
   * Parse error from various formats into AppError
   */
  const parseError = useCallback(
    (error: any, options: ErrorHandlerOptions = {}): AppError => {
      // Handle axios errors
      if (error?.response) {
        const statusCode = error.response.status;
        const data: ApiErrorResponse = error.response.data || {};

        const message =
          data.message ||
          data.error ||
          getMessageForStatusCode(statusCode, options.context);

        return {
          message,
          category: getCategoryFromStatusCode(statusCode),
          severity:
            statusCode >= 500 ? ErrorSeverity.CRITICAL : ErrorSeverity.ERROR,
          displayType: options.displayType || ErrorDisplayType.TOAST,
          statusCode,
          originalError: error,
          context: options.context,
          retryable: isRetryableError(statusCode),
          retryAction: options.retryAction,
        };
      }

      // Network error: an axios request was attempted but no response
      // came back. Requires the axios marker so plain thrown Errors do
      // not get misclassified as connection failures.
      if (error?.isAxiosError === true && !error?.response) {
        return {
          message: ERROR_MESSAGES.NETWORK.CONNECTION_LOST,
          category: ErrorCategory.NETWORK,
          severity: ErrorSeverity.ERROR,
          displayType: options.displayType || ErrorDisplayType.TOAST,
          originalError: error,
          context: options.context,
          retryable: true,
          retryAction: options.retryAction,
        };
      }

      // Handle string errors
      if (typeof error === 'string') {
        return {
          message: error,
          category: ErrorCategory.UNKNOWN,
          severity: ErrorSeverity.ERROR,
          displayType: options.displayType || ErrorDisplayType.TOAST,
          originalError: error,
          context: options.context,
          retryable: false,
        };
      }

      // Handle Error objects
      if (error instanceof Error) {
        return {
          message: error.message || ERROR_MESSAGES.GENERIC.UNKNOWN,
          category: ErrorCategory.UNKNOWN,
          severity: ErrorSeverity.ERROR,
          displayType: options.displayType || ErrorDisplayType.TOAST,
          originalError: error,
          context: options.context,
          retryable: false,
        };
      }

      // Fallback for unknown error types
      return {
        message: ERROR_MESSAGES.GENERIC.UNKNOWN,
        category: ErrorCategory.UNKNOWN,
        severity: ErrorSeverity.ERROR,
        displayType: options.displayType || ErrorDisplayType.TOAST,
        originalError: error,
        context: options.context,
        retryable: false,
      };
    },
    []
  );

  /**
   * Handle error with appropriate display strategy
   */
  const handleError = useCallback(
    (error: any, options: ErrorHandlerOptions = {}) => {
      const appError = parseError(error, options);

      // Log to console in development
      if (import.meta.env.DEV) {
        console.error('Error:', appError);
      }

      // Display based on strategy
      switch (appError.displayType) {
        case ErrorDisplayType.TOAST:
          if (options.showToast !== false) {
            showError(appError.message);
          }
          break;

        case ErrorDisplayType.PAGE:
        case ErrorDisplayType.INLINE:
          setGlobalError(appError);
          break;

        case ErrorDisplayType.SILENT:
          // Just logged, no UI display
          break;
      }

      // Call custom error handler if provided
      if (options.onError) {
        options.onError(appError);
      }

      return appError;
    },
    [parseError, showError, setGlobalError]
  );

  /**
   * Clear current error
   */
  const clearError = useCallback(() => {
    clearGlobalError();
  }, [clearGlobalError]);

  return {
    handleError,
    clearError,
    parseError,
  };
};
